import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLock,
  faArrowRight,
  faCheckCircle,
  faExclamationCircle,
  faShieldHalved,
  faEye,
  faEyeSlash,
  faHeadset,
} from '@fortawesome/free-solid-svg-icons';
import { setCredentials } from '../store/slices/authSlice';
import { api } from '../services/api';
import { useI18n } from '../hooks/useI18n';
import AuthLangSwitcher from '../components/AuthLangSwitcher';
import { openMailClient } from '../utils/mailSupport';
import '../styles/AuthModern.css';

const ResetPassword: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useI18n();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [status, setStatus] = useState<'form' | 'success' | 'error'>('form');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!token) {
      setError(t('auth.noTokenProvided'));
      setStatus('error');
      return;
    }
    if (!password || !confirmPassword) {
      setError(t('auth.fillAllFields'));
      return;
    }
    if (password !== confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/reset-password', { token, password });
      dispatch(setCredentials({ user: response.data.user, token: response.data.token }));
      setStatus('success');
      setTimeout(() => navigate('/'), 2000);
    } catch (err: any) {
      setStatus('error');
      setError(err.response?.data?.error || t('auth.resetFailed'));
    } finally {
      setLoading(false);
    }
  };

  // No token — invalid link
  if (!token) {
    return (
      <div className="auth-page">
        <div className="auth-status-layout">
          <div className="auth-status-card">
            <AuthLangSwitcher />
            <div className="auth-status-icon error">
              <FontAwesomeIcon icon={faExclamationCircle} />
            </div>
            <h2 className="auth-status-title error">{t('auth.invalidResetLink')}</h2>
            <p className="auth-status-desc">{t('auth.invalidResetDesc')}</p>
            <div className="auth-status-actions">
              <button
                className="auth-submit-btn"
                onClick={() => navigate('/forgot-password')}
                style={{ maxWidth: 320 }}
              >
                {t('auth.requestNewLink')}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
              <button className="auth-ghost-btn" onClick={() => navigate('/login')}>
                {t('auth.backToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Success state
  if (status === 'success') {
    return (
      <div className="auth-page">
        <div className="auth-status-layout">
          <div className="auth-status-card">
            <AuthLangSwitcher />
            <div className="auth-status-icon success">
              <FontAwesomeIcon icon={faCheckCircle} />
            </div>
            <h2 className="auth-status-title success">{t('auth.resetSuccess').split('!')[0]}!</h2>
            <p className="auth-status-desc">{t('auth.resetSuccess')}</p>
            <div className="auth-status-actions">
              <button
                className="auth-submit-btn"
                onClick={() => navigate('/')}
                style={{ maxWidth: 320 }}
              >
                {t('auth.goToDashboard')}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (status === 'error') {
    return (
      <div className="auth-page">
        <div className="auth-status-layout">
          <div className="auth-status-card">
            <AuthLangSwitcher />
            <div className="auth-status-icon error">
              <FontAwesomeIcon icon={faExclamationCircle} />
            </div>
            <h2 className="auth-status-title error">{t('auth.resetFailed')}</h2>
            <p className="auth-status-desc">{error}</p>
            <div className="auth-status-actions">
              <button
                className="auth-submit-btn"
                onClick={() => navigate('/forgot-password')}
                style={{ maxWidth: 320 }}
              >
                {t('auth.tryAgain')}
                <FontAwesomeIcon icon={faArrowRight} />
              </button>
              <button className="auth-ghost-btn" onClick={() => navigate('/login')}>
                {t('auth.backToLogin')}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Form state
  return (
    <div className="auth-page">
      <div className="auth-wrapper">
        {/* Left Branding */}
        <div className="auth-brand-panel">
          <div className="auth-brand-logo">
            <div className="auth-brand-logo-icon">E</div>
            <div className="auth-brand-logo-text">ELVOAQ</div>
          </div>

          <h2 className="auth-brand-heading">
            {t('auth.createNewPassword').split(' ').slice(0, 1).join(' ')}{' '}
            <span>{t('auth.createNewPassword').split(' ').slice(1).join(' ')}</span>
          </h2>
          <p className="auth-brand-desc">{t('auth.createNewPasswordDesc')}</p>

          <div className="auth-features-list">
            <div className="auth-feature-item">
              <div className="auth-feature-icon">
                <FontAwesomeIcon icon={faShieldHalved} />
              </div>
              <span className="auth-feature-text">{t('auth.secureLogin')}</span>
            </div>
          </div>
        </div>

        {/* Right Form */}
        <div className="auth-form-panel">
          <div className="auth-card">
            <AuthLangSwitcher />

            <div className="auth-form-header">
              <h1>{t('auth.resetPasswordTitle')}</h1>
              <p>{t('auth.resetPasswordSubtitle')}</p>
            </div>

            {error && (
              <div className="auth-error">
                <FontAwesomeIcon icon={faExclamationCircle} />
                <span>{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="auth-form">
              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faLock} />
                  {t('auth.newPassword')}
                </label>
                <div className="auth-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="auth-input"
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setError(''); }}
                    placeholder={t('auth.passwordPlaceholder')}
                    disabled={loading}
                    autoComplete="new-password"
                    autoFocus
                    style={{ paddingRight: '50px' }}
                  />
                  <button
                    type="button"
                    className="auth-input-icon"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                  </button>
                </div>
              </div>

              <div className="auth-form-group">
                <label className="auth-form-label">
                  <FontAwesomeIcon icon={faLock} />
                  {t('auth.confirmNewPassword')}
                </label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  className="auth-input"
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e.target.value); setError(''); }}
                  placeholder={t('auth.passwordPlaceholder')}
                  disabled={loading}
                  autoComplete="new-password"
                />
              </div>

              <button type="submit" className="auth-submit-btn" disabled={loading}>
                {loading ? (
                  <>
                    <span className="auth-spinner" />
                    {t('auth.resetting')}
                  </>
                ) : (
                  <>
                    {t('auth.resetPassword')}
                    <FontAwesomeIcon icon={faArrowRight} />
                  </>
                )}
              </button>
            </form>

            <div className="auth-support-section">
              <p className="auth-support-text">{t('auth.havingProblems')}</p>
              <button
                type="button"
                className="auth-support-btn"
                onClick={() => openMailClient(t('auth.supportSubject'), t('auth.supportBody'))}
              >
                <FontAwesomeIcon icon={faHeadset} />
                {t('auth.reachOutToUs')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
